import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Wallet, Plus, Trash2, FileDown, CheckSquare, Square, ArrowLeftCircle, RotateCcw, ShieldCheck, ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, ChevronDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { useProjectFilter } from "@/lib/projectFilter";
import { useRefresh } from "@/lib/useRefresh";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SearchableMultiSelect from "@/components/SearchableMultiSelect";
import SearchableSelect from "@/components/SearchableSelect";
import ConfirmDialog from "@/components/ConfirmDialog";
import { formatIDR, fmtDate, buildKetidakpastianTooltip } from "@/lib/constants";
import { getExportColumns, getExportColors, exportPettyCashReport, exportPettyCashReportPdf } from "@/lib/kasBankEstatement";
import NewPettyCashDialog from "@/components/petty-cash/NewPettyCashDialog";
import PettyCashExportDialog from "@/components/petty-cash/PettyCashExportDialog";
import PettyCashAuditTrailViewer from "@/components/petty-cash/PettyCashAuditTrailViewer";
import PettyCashRow from "@/components/petty-cash/PettyCashRow";
import PettyCashDetailDialog from "@/components/petty-cash/PettyCashDetailDialog";
import PettyCashReviewDialog from "@/components/petty-cash/PettyCashReviewDialog";
import PettyCashTransferDialog from "@/components/petty-cash/PettyCashTransferDialog";
import PettyCashActualDialog from "@/components/petty-cash/PettyCashActualDialog";
import PettyCashReallocateDialog from "@/components/petty-cash/PettyCashReallocateDialog";
import PettyCashEditDialog from "@/components/petty-cash/PettyCashEditDialog";
import PettyCashReturnDialog from "@/components/petty-cash/PettyCashReturnDialog";
import PettyCashReturnsHistory from "@/components/petty-cash/PettyCashReturnsHistory";
import StaffCashLedgerDialog from "@/components/StaffCashLedgerDialog";
import { projectLabel } from "@/components/petty-cash/utils";

function rowProjectLabel(pc) {
  // 2026-02-27 (PROMPT 16-PETTY-CASH-OFFICE-COST-CENTER-R1): office cost centers
  // ditampilkan dgn badge "Kantor" dan nama lokasi (bukan project_name lama).
  if (pc.cost_center_type === "office") return pc.cost_center_name || "Kantor";
  if (pc.project_type === "variation_order") return `${pc.project_name} · VO ${pc.project_variation || ""}`.trim();
  if (pc.project_type === "working_order") return `${pc.project_name} · WO ${pc.project_wo_number || ""}`.trim();
  return pc.project_name || "Untitled";
}

// 2026-09-29 + 2026-10-13 (Tahap 2): sel AKUN COA di tabel Report bisa dikoreksi
// pemeriksa Verifikasi 2 — ikon pensil → dropdown COA searchable → simpan.
// Mengubah klasifikasi yang SUDAH ADA wajib alasan (audit old→new + reason).
function CoaCellEditor({ txn, coaOptions, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  if (txn.kind !== "out" || !txn.item_id || !canEdit) return <>{txn.coa_code || (txn.unclassified ? "Belum Diklasifikasikan" : "—")}</>;
  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className={txn.unclassified ? "text-amber-700 italic" : ""}>{txn.coa_code || (txn.unclassified ? "Belum Diklasifikasikan" : "—")}</span>
        <button type="button" title="Ubah akun COA (Verifikasi 2)" data-testid={`pc-report-coa-edit-${txn.item_id}`}
          onClick={() => { setEditing(true); setPending(null); setReason(""); }}
          className="p-0.5 text-slate-400 hover:text-[#0B1D3A] hover:bg-slate-200 rounded">
          <Pencil size={10} />
        </button>
      </span>
    );
  }
  const needsReason = !!txn.coa_id && pending && pending !== txn.coa_id;
  const save = async (v, rsn) => {
    if (!v || v === txn.coa_id) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/petty-cash/${txn.pc_id}/items/${txn.item_id}/coa`, { coa_id: v, reason: rsn || null });
      toast.success("Akun COA diperbarui");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
      setSaving(false);
    }
  };
  const change = (v) => {
    if (!v || v === txn.coa_id) { setEditing(false); return; }
    if (txn.coa_id) { setPending(v); return; }  // klasifikasi existing → wajib alasan dulu
    save(v, null);
  };
  return (
    <div className="min-w-[180px] space-y-1" data-testid={`pc-report-coa-editor-${txn.item_id}`}>
      <SearchableSelect
        value={txn.coa_id || ""}
        onChange={change}
        options={coaOptions}
        placeholder="Pilih Akun COA"
        searchPlaceholder="Ketik kode / nama akun…"
        emptyText="Akun tidak ditemukan"
        disabled={saving}
        testid={`pc-report-coa-select-${txn.item_id}`}
      />
      {needsReason && (
        <div className="flex gap-1">
          <Input
            data-testid={`pc-report-coa-reason-${txn.item_id}`}
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            placeholder="Alasan ubah klasifikasi *"
            className="rounded-none h-7 text-xs"
            autoFocus
          />
          <button
            type="button"
            data-testid={`pc-report-coa-save-${txn.item_id}`}
            onClick={() => reason.trim() ? save(pending, reason.trim()) : toast.error("Alasan wajib diisi")}
            disabled={saving}
            className="px-2 text-[10px] font-semibold uppercase text-white bg-emerald-600 hover:bg-emerald-700 shrink-0"
          >Simpan</button>
        </div>
      )}
    </div>
  );
}

export default function PettyCashPage({ embedded = false }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const { projects } = useProjectFilter();
  // 149-cw-r159: deep-link `?open=<id>` — buka PettyCashDetailDialog otomatis.
  const [searchParams, setSearchParams] = useSearchParams();
  // 2026-06-20 (user request): sub-tab di dalam Weekly Petty Cash — "Request"
  // (konten existing) & "Report" (rekap per proyek per orang). Deep-link ?view=.
  const view = searchParams.get("view") === "report" ? "report" : "request";
  const setView = (v) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    if (v === "report") p.set("view", "report"); else p.delete("view");
    return p;
  }, { replace: true });
  const [rows, setRows] = useState([]);
  // 149-cw-r160: highlight item id dari `?comment=<item_id>` — passed ke
  // PettyCashDetailDialog utk scroll ke item spesifik.
  const [highlightItemId, setHighlightItemId] = useState(null);
  const [projectFilter, setProjectFilter] = useState("all");
  // 2026-06-10: filter multi staff (pengaju) — deklarasi state HARUS sebelum
  // `load` (TDZ: const tidak bisa diakses sebelum inisialisasi).
  const [userFilter, setUserFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [reviewAction, setReviewAction] = useState("approve");
  const [reviewNote, setReviewNote] = useState("");
  const [transferring, setTransferring] = useState(null);
  const [transferDate, setTransferDate] = useState("");
  const [transferNote, setTransferNote] = useState("");
  const [transferBankId, setTransferBankId] = useState("");
  const [transferBankOptions, setTransferBankOptions] = useState([]);
  const [transferSourceKey, setTransferSourceKey] = useState("");
  // 2026-09-23: nominal transfer aktual dari preview (pengajuan − saldo pengaju;
  // bisa 0 bila saldo mencukupi) — dikirim ke backend supaya tercatat akurat.
  const [transferAmount, setTransferAmount] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [showReturnsHistory, setShowReturnsHistory] = useState(false);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  // Actual expense — per-item state (setelah transfer, isi realisasi per item)
  const [actualPc, setActualPc] = useState(null);
  const [actualItems, setActualItems] = useState({}); // { [item_id]: { actual_amount, actual_transaction_date, note } }
  const [actualDocNote, setActualDocNote] = useState("");
  // Saldo pengaju (Total Diterima − Pengeluaran Realisasi)
  const [myBalance, setMyBalance] = useState(null);
  const navigate = useNavigate();

  const isFinanceLead = !!user && canPerm(user, "petty_cash", "update");
  const isBOD = user?.division === "BOD";
  // 16-PETTY-CASH-APPROVAL-ACCESS-CONTROL-R1: compute granular perms untuk gating tombol.
  // Backend adalah source of truth (setiap endpoint tetap validasi via _pc_can). UI hanya
  // menyembunyikan tombol untuk UX yang bersih; user tanpa perm yang memanggil endpoint
  // via curl/postman tetap 403.
  const pcPerms = useMemo(() => ({
    view:              canPerm(user, "petty_cash", "view"),
    create:            canPerm(user, "petty_cash", "create"),
    delete:            canPerm(user, "petty_cash", "delete"),
    approve:           canPerm(user, "petty_cash", "approve"),
    reject:            canPerm(user, "petty_cash", "reject"),
    edit_own:          canPerm(user, "petty_cash", "edit_own"),
    edit_all:          canPerm(user, "petty_cash", "edit_all"),
    assign_approver:   canPerm(user, "petty_cash", "assign_approver"),
    reopen:            canPerm(user, "petty_cash", "reopen"),
    restore:           canPerm(user, "petty_cash", "restore"),
    verify_actual:     canPerm(user, "petty_cash", "verify_actual"),
    // 2026-10-14 (user request): Verifikasi 2 — penetapan akun COA (antrean terpisah).
    verify_coa:        canPerm(user, "petty_cash", "verify_coa"),
    // 149-cw-r180: tahapan Check pasca-verifikasi — perm granular terpisah.
    check:             canPerm(user, "petty_cash", "check"),
    confirm_transfer:  canPerm(user, "petty_cash", "confirm_transfer"),
    export:            canPerm(user, "petty_cash", "export"),
    self_approve:      canPerm(user, "petty_cash", "self_approve"),
  }), [user, canPerm]);
  const [reviewerOptions, setReviewerOptions] = useState([]);  // FAT L2 candidates for BOD to assign
  const [editingPc, setEditingPc] = useState(null); // full pc row being edited by reviewer
  const [editItems, setEditItems] = useState([]);
  const [editNote, setEditNote] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // 16-PC-AUDIT-TRAIL-VIEWER (2026-08-26): dedicated forensik viewer utk petty cash.
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);
  const [auditTrailPcId, setAuditTrailPcId] = useState(null);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const bulkApproveSelected = async () => {
    const items = rows.filter((r) => selectedIds.has(r.id) && r.status === "pending" && r.user_id !== user?.id);
    if (!items.length) { toast.error("Tidak ada pengajuan pending yang dipilih"); return; }
    if (!window.confirm(`Approve ${items.length} petty cash sekaligus?`)) return;
    setBulkBusy(true);
    let ok = 0; let fail = 0;
    for (const it of items) {
      try {
        await api.patch(`/petty-cash/${it.id}/review`, { action: "approve", note: "Bulk approve" });
        ok += 1;
      } catch (e) { fail += 1; }
    }
    toast.success(`Approve selesai: ${ok} berhasil${fail ? `, ${fail} gagal` : ""}`);
    setSelectedIds(new Set());
    setBulkBusy(false);
    await load();
  };

  // 149-cw-r189: Rekap sisa/total saldo SEMUA staff — hanya untuk user dgn akses
  // approve (backend /petty-cash/staff-summary juga di-gate approve).
  const [staffSummary, setStaffSummary] = useState(null);
  // 2026-06 (user request): rekap saldo staff — nama jadi dropdown breakdown per project.
  const [expandedStaff, setExpandedStaff] = useState({});
  const toggleStaffExpand = (uid) => setExpandedStaff((prev) => ({ ...prev, [uid]: !prev[uid] }));
  // User request (2026-06-10): row Rekap Saldo Staff clickable → ledger detail
  // per item (uang masuk / uang keluar) via StaffCashLedgerDialog.
  // 2026-09-23 (user request): detail dibuka per PROJECT (bukan klik nama staff).
  const [ledgerUserId, setLedgerUserId] = useState(null);
  // 2026-10-14 (user request — isolasi saldo per proyek): simpan {id, name}
  // supaya ledger difilter berdasarkan project_id (stabil), bukan nama.
  const [ledgerProject, setLedgerProject] = useState(null);
  const canApprovePc = pcPerms.approve;
  useEffect(() => {
    if (!canApprovePc) { setStaffSummary(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/petty-cash/staff-summary");
        setStaffSummary(data);
      } catch { setStaffSummary(null); }
    })();
  }, [canApprovePc, rows]);

  const load = useCallback(async () => {
    try {
      const params = projectFilter !== "all" ? { project_id: projectFilter } : {};
      // 2026-06-10: filter multi staff (pengaju).
      if (userFilter !== "all") params.user_id = userFilter;
      // BOD selalu ambil termasuk soft-deleted, biar tab "Trash" bisa diisi tanpa refetch
      if (isBOD) params.include_deleted = true;
      const { data } = await api.get("/petty-cash", { params });
      setRows(data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, [projectFilter, userFilter, isBOD]);
  useEffect(() => { load(); }, [load]);

  // 149-cw-r159: consume deep-link `?open=<id>` — buka PettyCashDetailDialog.
  // 149-cw-r160: `?comment=<item_id>` — scroll ke line item spesifik.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || rows.length === 0) return;
    const found = rows.find((r) => r.id === openId);
    if (!found) {
      // Deep-link menunjuk row yg sudah terhapus / di luar scope — beri tahu user.
      toast.info("Item tidak ditemukan — mungkin sudah dihapus atau di luar akses Anda.", { id: "deeplink-not-found" });
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      next.delete("comment");
      setSearchParams(next, { replace: true });
      return;
    }
    setDetail(found);
    setHighlightItemId(searchParams.get("comment") || null);
    // Clear query param supaya refresh tidak trigger ulang.
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    next.delete("comment");
    setSearchParams(next, { replace: true });
  }, [searchParams, rows, setSearchParams]);
  useRefresh(load, [projectFilter, userFilter, isBOD]);

  // Saldo staff SELALU total lintas semua project (permintaan user 08-Feb).
  // Tidak lagi di-scope oleh projectFilter — angka "uang yang diterima" dan
  // "sisa saldo" mewakili total kas petty cash staff seluruhnya.
  const loadBalance = useCallback(async () => {
    try {
      const { data } = await api.get("/petty-cash/balance");
      setMyBalance(data);
    } catch (e) { setMyBalance(null); }
  }, []);
  useEffect(() => { loadBalance(); }, [loadBalance, rows]);

  const restoreDeleted = async (pc) => {
    if (!window.confirm(`Kembalikan pengajuan ${pc.week_label} milik ${pc.user_name}?`)) return;
    try {
      await api.post(`/petty-cash/${pc.id}/restore`);
      toast.success("Pengajuan dikembalikan");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const purgeOldTrash = async () => {
    const raw = window.prompt("Purge soft-deleted lebih tua dari berapa hari?", "30");
    if (raw === null) return;
    const days = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(days) || days < 1) { toast.error("Hari harus angka positif"); return; }
    if (!window.confirm(`Hard-delete permanen SEMUA pengajuan yang sudah dihapus lebih dari ${days} hari lalu? Aksi ini tidak bisa di-undo, tapi setiap purge tetap tercatat di audit log.`)) return;
    try {
      const { data } = await api.post(`/petty-cash/trash/purge?older_than_days=${days}`);
      toast.success(`Purge selesai: ${data.purged} pengajuan permanen dihapus (>${days} hari)`);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  // Load FAT L2 candidates once for BOD (to assign as reviewer)
  useEffect(() => {
    if (!isBOD) return;
    (async () => {
      try {
        const { data } = await api.get("/users");
        setReviewerOptions((data || []).filter((u) => u.division === "FAT" && u.level === 2));
      } catch (e) { console.warn("Failed to load FAT-L2 reviewer options", e); }
    })();
  }, [isBOD]);

  const assignReviewer = async (pcid, rid) => {
    try {
      await api.patch(`/petty-cash/${pcid}/assign-reviewer`, { reviewer_id: rid || null });
      toast.success(rid ? "Reviewer ditugaskan" : "Reviewer dibatalkan");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const counts = useMemo(() => {
    const live = rows.filter((r) => !r.deleted_at);
    return {
      all: live.length,
      pending: live.filter((r) => r.status === "pending").length,
      approved: live.filter((r) => r.status === "approved").length,
      approved_not_transferred: live.filter((r) => r.status === "approved" && !r.transfer_date).length,
      rejected: live.filter((r) => r.status === "rejected").length,
      deleted: rows.filter((r) => r.deleted_at).length,
      actual_incomplete: live.filter((r) => r.transferred_at && !r.v1_complete && (r.v1_incomplete_items || 0) > 0).length,
    };
  }, [rows]);

  const visible = useMemo(() => {
    if (statusFilter === "deleted") return rows.filter((r) => r.deleted_at);
    const live = rows.filter((r) => !r.deleted_at);
    if (statusFilter === "all") return live;
    if (statusFilter === "awaiting_transfer") return live.filter((r) => r.status === "approved" && !r.transfer_date);
    if (statusFilter === "actual_incomplete") return live.filter((r) => r.transferred_at && !r.v1_complete && (r.v1_incomplete_items || 0) > 0);
    return live.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  // 149-cw-r193: filter project multi-select — state tetap string ("all" atau
  // comma-joined ids) supaya call site lain (load, export) minimal berubah.
  const pfIds = projectFilter === "all" ? [] : projectFilter.split(",").filter(Boolean);
  const setPfIds = (ids) => setProjectFilter(ids.length ? ids.join(",") : "all");
  // 149-cw-r196: nama project terpilih — dijabarkan di dialog export.
  const pfNames = pfIds.map((id) => projects.find((p) => p.id === id)?.project_name).filter(Boolean);

  // 2026-06-10 (user request): filter multi staff (pengaju) di sebelah filter
  // project — sinkron dgn export (staff terpilih ikut ter-export).
  const [submitters, setSubmitters] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/petty-cash/submitters"); setSubmitters(data || []); }
      catch { setSubmitters([]); }
    })();
  }, []);
  const ufIds = userFilter === "all" ? [] : userFilter.split(",").filter(Boolean);
  const setUfIds = (ids) => setUserFilter(ids.length ? ids.join(",") : "all");
  const ufNames = ufIds.map((id) => submitters.find((s) => s.user_id === id)?.user_name).filter(Boolean);

  // 149-cw-r195 (user request): judul kolom tabel bisa di-sort (klik = asc,
  // klik lagi = desc). Default tanpa sort = urutan server (terbaru dulu).
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const _weekKey = (r) => {
    const m = String(r.week_label || "").match(/(\d{4})-W(\d+)/i);
    return m ? parseInt(m[1], 10) * 100 + parseInt(m[2], 10) : 0;
  };
  const SORTERS = {
    week: (a, b) => _weekKey(a) - _weekKey(b),
    project: (a, b) => rowProjectLabel(a).localeCompare(rowProjectLabel(b), "id"),
    submitter: (a, b) => String(a.user_name || "").localeCompare(String(b.user_name || ""), "id"),
    total: (a, b) => (a.total || 0) - (b.total || 0),
    status: (a, b) => String(a.status || "").localeCompare(String(b.status || "")),
    transfer: (a, b) => String(a.transfer_date || a.transferred_at || "").localeCompare(String(b.transfer_date || b.transferred_at || "")),
  };
  const sortedVisible = useMemo(() => {
    const cmp = sortKey ? SORTERS[sortKey] : null;
    if (!cmp) return visible;
    const arr = [...visible].sort(cmp);
    return sortDir === "desc" ? arr.reverse() : arr;
  }, [visible, sortKey, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps
  const SortTh = ({ label, k, className = "text-left" }) => (
    <th className={`${className} px-3 py-2`}>
      <button
        type="button"
        data-testid={`pc-sort-${k}`}
        onClick={() => {
          if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
          else { setSortKey(k); setSortDir("asc"); }
        }}
        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-[#0B1D3A] transition-colors"
        title={`Urutkan berdasarkan ${label}`}
      >
        {label}
        {sortKey === k
          ? (sortDir === "asc" ? <ArrowUp size={11} className="text-[#FF5A1F]" /> : <ArrowDown size={11} className="text-[#FF5A1F]" />)
          : <ArrowUpDown size={11} className="opacity-40" />}
      </button>
    </th>
  );

  const submitReview = async () => {
    if (!reviewing) return;
    try {
      await api.patch(`/petty-cash/${reviewing.id}/review`, { action: reviewAction, note: reviewNote });
      toast.success(`Petty cash ${reviewAction === "approve" ? "disetujui" : "ditolak"}`);
      setReviewing(null); setReviewNote("");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const submitEditPc = async () => {
    if (!editingPc) return;
    if (!editNote.trim()) { toast.error("Catatan perubahan wajib diisi"); return; }
    if (!editItems.length || editItems.some((it) => !it.name?.trim())) { toast.error("Nama item wajib diisi"); return; }
    try {
      await api.patch(`/petty-cash/${editingPc.id}/edit`, {
        items: editItems.map((it) => ({
          name: it.name.trim(),
          qty: Number(it.qty) || 1,
          unit_price: Number(String(it.unit_price).replace(/[^\d]/g, "")) || 0,
          note: (it.note || "").trim() || null,
        })),
        edit_note: editNote.trim(),
      });
      toast.success("Petty cash diperbarui");
      setEditingPc(null); setEditItems([]); setEditNote("");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const submitTransfer = async () => {
    if (!transferring) return;
    if (!transferDate) { toast.error("Tanggal transfer wajib diisi"); return; }
    let sourcePayload = {};
    if (transferSourceKey === "cash") {
      sourcePayload = { source_type: "cash" };
    } else if (transferSourceKey.startsWith("staff:")) {
      const [, userId, accountId] = transferSourceKey.split(":");
      sourcePayload = { source_type: "staff_bank", source_staff_user_id: userId, source_staff_account_id: accountId };
    } else if (transferSourceKey.startsWith("company:")) {
      sourcePayload = { source_type: "company_bank", source_company_bank_account_id: transferSourceKey.slice(8) };
    }
    try {
      await api.patch(`/petty-cash/${transferring.id}/transfer`, {
        transfer_date: transferDate,
        note: transferNote,
        bank_account_id: transferBankId || null,
        transfer_amount: transferAmount, // dari preview (boleh 0 bila saldo mencukupi)
        ...sourcePayload,
      });
      toast.success("Transfer ditandai");
      setTransferring(null); setTransferDate(""); setTransferNote(""); setTransferBankId(""); setTransferBankOptions([]); setTransferSourceKey(""); setTransferAmount(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const openDetailDialog = (r) => setDetail(r);

  // 149-cw-r185: Pengalihan Kas — dialog terpisah utk mengubah item pengajuan
  // menjadi banyak item realisasi setelah uang ditransfer.
  const [reallocPc, setReallocPc] = useState(null);
  const openReallocDialog = (r) => setReallocPc(r);
  const submitRealloc = async (payload) => {
    if (!reallocPc) return;
    try {
      await api.post(`/petty-cash/${reallocPc.id}/reallocate`, payload);
      toast.success("Pengalihan kas tersimpan");
      setReallocPc(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal mengalihkan"); }
  };

  const openActualDialog = (r) => {
    setActualPc(r);
    // Pre-fill per-item state: default actual_amount = subtotal, date = today or transfer_date
    const defaultDate = r.transfer_date || new Date().toISOString().slice(0, 10);
    const initial = {};
    (r.items || []).forEach((it, i) => {
      // 2026-06-10 (bug user): item legacy tanpa `id` — fallback key per-index.
      // Tanpa ini semua row berbagi key `undefined` → isi nominal satu item
      // mengubah SEMUA baris serentak.
      const rowKey = it.id || `idx-${i}`;
      initial[rowKey] = {
        actual_amount: it.actual_amount != null ? String(it.actual_amount) : String(it.subtotal || 0),
        actual_transaction_date: it.actual_transaction_date || defaultDate,
        note: it.actual_note || "",
        // 149-cw-r190: prefill bukti transaksi yg sudah tersimpan.
        voucher_no: it.voucher_no || "",
        voucher_date: it.voucher_date || "",
        supplier: it.supplier || "",
      };
    });
    setActualItems(initial);
    setActualDocNote(r.actual_note || "");
  };

  const setActualItemField = (itemId, field, value) => {
    setActualItems((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [field]: value } }));
  };

  const submitActual = async () => {
    if (!actualPc) return;
    const items = Object.entries(actualItems).map(([key, v]) => ({
      // item legacy tanpa id → kirim item_index supaya backend match posisional.
      item_id: key.startsWith("idx-") ? null : key,
      item_index: key.startsWith("idx-") ? parseInt(key.slice(4), 10) : null,
      actual_amount: Number(String(v.actual_amount ?? "").replace(/[^\d.-]/g, "")) || 0,
      actual_transaction_date: v.actual_transaction_date,
      note: v.note || null,
      // 149-cw-r190: nomor voucher & toko/supplier per item.
      voucher_no: (v.voucher_no || "").trim() || null,
      voucher_date: v.voucher_date || null,
      supplier: (v.supplier || "").trim() || null,
    }));
    if (!items.length) { toast.error("Tidak ada item untuk diisi"); return; }
    for (const it of items) {
      if (!it.actual_transaction_date) { toast.error("Tanggal transaksi wajib diisi di setiap item"); return; }
      if (!it.voucher_date) { toast.error("Tanggal voucher wajib diisi di setiap item"); return; }
      if (it.actual_amount < 0) { toast.error("Nominal aktual tidak boleh negatif"); return; }
    }
    try {
      await api.patch(`/petty-cash/${actualPc.id}/actual`, {
        items,
        note: actualDocNote || null,
      });
      toast.success("Realisasi per item tersimpan. Menunggu Verifikasi 1 oleh FAT.");
      setActualPc(null); setActualItems({}); setActualDocNote("");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  // Approver clicks the row's "Verifikasi" button → open Detail dialog which now hosts
  // the per-item Verify / Reject buttons on the right side of each row.
  const verifyActual = (r) => setDetail(r);

  // Load submitter's bank accounts when transfer dialog opens
  useEffect(() => {
    if (!transferring) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${transferring.user_id}/bank-accounts`);
        setTransferBankOptions(data || []);
        // Default to the account submitter picked when creating; else primary (first)
        const chosen = transferring.bank_account?.id;
        const found = chosen && (data || []).find((a) => a.id === chosen);
        setTransferBankId(found ? found.id : (data?.[0]?.id || ""));
      } catch (e) { setTransferBankOptions([]); }
    })();
  }, [transferring]);

  const doDelete = async () => {
    if (!confirmDel) return;
    const isBODLocal = user?.division === "BOD";
    const isOwnerLocal = confirmDel.user_id === user?.id;
    // For BOD deleting someone else's pengajuan, ask for an optional reason so the
    // audit log has traceable context. Owner-pengaju hard delete on pending doesn't prompt.
    let reason = null;
    if (isBODLocal && !isOwnerLocal) {
      reason = window.prompt(
        `Hapus pengajuan ${confirmDel.week_label} milik ${confirmDel.user_name}?\n\nAlasan (opsional, akan tersimpan di audit log):`,
        ""
      );
      if (reason === null) return;  // user pressed Cancel
    }
    try {
      const url = `/petty-cash/${confirmDel.id}` + (reason ? `?reason=${encodeURIComponent(reason.trim())}` : "");
      const { data } = await api.delete(url);
      toast.success(data?.mode === "soft" ? "Dihapus (soft delete, tercatat di audit log)" : "Dihapus");
      setConfirmDel(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  // 2026-09-24 (user request): Report = tabel transaksi dgn 12 kolom yang SAMA
  // seperti tab Breakdown / template export Buku Bank (DATE · INVOICE/PI NO ·
  // NO PO · KODE VOUCHER · DESCRIPTION · CREDIT (IN) · DEBIT (OUT) · SALDO ·
  // PIC/Vendor · Keterangan Pembelian · Proyek · AKUN COA) supaya mudah
  // disinkronkan dgn data bank. Label kolom memakai config yang sama
  // (estatement_export_config) — rename di tab Breakdown ikut terbawa.
  const PC_LEDGER_DEFAULT_COLS = [
    { key: "date", label: "DATE" },
    { key: "invoice_no", label: "INVOICE / PI NO" },
    { key: "po_no", label: "NO Purchase Order" },
    { key: "doc_no", label: "KODE VOUCHER" },
    { key: "description", label: "DESCRIPTION" },
    { key: "credit", label: "CREDIT (IN)" },
    { key: "debit", label: "DEBIT (OUT)" },
    { key: "balance", label: "SALDO" },
    { key: "pic_name", label: "PIC / Vendor Name" },
    { key: "keterangan_pembelian", label: "Keterangan Pembelian" },
    { key: "project_name", label: "Proyek" },
    { key: "coa", label: "AKUN COA" },
  ];
  const PC_NUM_COLS = new Set(["credit", "debit", "balance"]);
  const [pcCols, setPcCols] = useState(PC_LEDGER_DEFAULT_COLS);
  // Warna baris mengikuti pengaturan export (Settings → Warna Export).
  const [pcColors, setPcColors] = useState({ green: "92D050", purple: "C58BEF" });
  // Daftar COA utk editor inline kolom AKUN COA di tabel Report (verifikator saja).
  const [coaOptions, setCoaOptions] = useState([]);
  useEffect(() => {
    if (view !== "report" || !isFinanceLead) return;
    api.get("/finance/chart-of-accounts")
      .then((r) => setCoaOptions((Array.isArray(r.data) ? r.data : []).filter((c) => !c.is_parent).map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))))
      .catch(() => setCoaOptions([]));
  }, [view, isFinanceLead]);
  // 2026-09-29 (user request): penyesuaian kolom KHUSUS tabel Report ini —
  // "DATE"→"Tanggal Transaksi", "INVOICE / PI NO"→"Tanggal Vocher", dan kolom
  // "NO PURCHASE ORDER" (po_no) dihapus. Config shared Breakdown tidak diubah.
  const pcReportCols = useMemo(() => {
    const RELABEL = { date: "Tanggal Transaksi", invoice_no: "Tanggal Vocher" };
    return pcCols
      .filter((c) => c.key !== "po_no")
      .flatMap((c) => {
        // 2026-09-29 (user request): kolom AKUN COA dipecah → KODE AKUN + NAMA AKUN.
        if (c.key === "coa") return [{ key: "coa_code", label: "KODE AKUN" }, { key: "coa_name", label: "NAMA AKUN" }];
        const rel = RELABEL[c.key] ? { ...c, label: RELABEL[c.key] } : c;
        return [rel];
      });
  }, [pcCols]);
  const [exportingReport, setExportingReport] = useState(false);
  useEffect(() => {
    getExportColumns().then((c) => { if (Array.isArray(c) && c.length) setPcCols(c); }).catch(() => {});
    getExportColors().then((c) => { if (c) setPcColors((p) => ({ ...p, ...c })); }).catch(() => {});
  }, []);
  const pcHexA = (h, a) => {
    const n = parseInt(h || "000000", 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
  };

  // Ledger transaksi per proyek → per staff. Baris "in" = transfer pengisian kas
  // (CREDIT), baris "out" = realisasi item (DEBIT). SALDO = saldo berjalan per staff.
  // 2026-09-30 (user request): filter rentang tanggal & pencarian transaksi di tab Report.
  const [reportDateFrom, setReportDateFrom] = useState("");
  const [reportDateTo, setReportDateTo] = useState("");
  const [reportSearch, setReportSearch] = useState("");
  const reportLedger = useMemo(() => {
    const byProject = new Map();
    const _repFrom = reportDateFrom, _repTo = reportDateTo;
    const _repKw = reportSearch.trim().toLowerCase();
    const _reportInRange = (d) => (!_repFrom || (!!d && d >= _repFrom)) && (!_repTo || (!!d && d <= _repTo));
    const _reportMatchKw = (t, userName, projName) =>
      !_repKw
      || String(userName || "").toLowerCase().includes(_repKw)
      || String(projName || "").toLowerCase().includes(_repKw)
      || [t.description, t.doc_no, t.ket, t.pic, t.coa_code, t.coa_name, t.voucher_date].some((v) => String(v || "").toLowerCase().includes(_repKw));
    for (const r of rows) {
      // Pengajuan soft-deleted (tab Trash) tidak boleh ikut di Report.
      if (r.deleted_at) continue;
      const proj = rowProjectLabel(r);
      const uid = r.user_id || r.user_name || "-";
      if (!byProject.has(proj)) byProject.set(proj, new Map());
      const byUser = byProject.get(proj);
      if (!byUser.has(uid)) byUser.set(uid, {
        user_name: r.user_name || "-", division: r.user_division || r.division || "-", txns: [],
      });
      const u = byUser.get(uid);
      if (r.transferred_at && Number(r.transfer_amount ?? r.total ?? 0) > 0) {
        const tIn = {
          kind: "in", date: (r.transfer_date || r.transferred_at || "").slice(0, 10),
          doc_no: r.week_label || "", description: `Pengisian Petty Cash ${r.week_label || ""}`.trim(),
          credit: Number(r.transfer_amount ?? r.total ?? 0), debit: 0,
          pic: r.user_name || "-", ket: r.transfer_note || "",
          project: proj, coa: r.cost_center_name || "",
        };
        if (_reportInRange(tIn.date) && _reportMatchKw(tIn, u.user_name, proj)) u.txns.push(tIn);
      }
      for (const it of (r.items || [])) {
        if (it.is_cancelled || it.actual_amount === null || it.actual_amount === undefined) continue;
        const tOut = {
          kind: "out", date: (it.actual_transaction_date || r.actual_transaction_date || "").slice(0, 10),
          doc_no: it.voucher_no || "", description: it.name || "Item",
          credit: 0, debit: Number(it.actual_amount || 0),
          pic: it.supplier || r.user_name || "-", ket: it.actual_note || it.note || "",
          // 2026-09-29 + 2026-10-13 (Tahap 2): AKUN COA item dari Verifikasi 2;
          // item lolos V1 tanpa COA tampil "Belum Diklasifikasikan" (bukan hilang
          // dari laporan); fallback cost center hanya utk data belum V1/legacy.
          project: proj, coa: it.coa_code ? `${it.coa_code} — ${it.coa_name || ""}` : (it.item_verified === true ? "Belum Diklasifikasikan" : (r.cost_center_name || "")),
          coa_code: it.coa_code || "", coa_name: it.coa_name || (it.coa_code ? "" : (it.item_verified === true ? "Belum Diklasifikasikan" : (r.cost_center_name || ""))),
          unclassified: !it.coa_code && it.item_verified === true,
          voucher_date: it.voucher_date || "",
          pc_id: r.id, item_id: it.id, coa_id: it.coa_id || "",
        };
        if (_reportInRange(tOut.date) && _reportMatchKw(tOut, u.user_name, proj)) u.txns.push(tOut);
      }
    }
    const groups = [...byProject.entries()].map(([project, byUser]) => {
      const persons = [...byUser.values()].map((u) => {
        u.txns.sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.kind === "in" ? -1 : 1));
        let running = 0;
        for (const t of u.txns) { running += t.credit - t.debit; t.saldo = running; }
        const inSum = u.txns.reduce((a, t) => a + t.credit, 0);
        const outSum = u.txns.reduce((a, t) => a + t.debit, 0);
        return { ...u, inSum, outSum, saldo: running };
      }).filter((u) => u.txns.length > 0).sort((a, b) => a.user_name.localeCompare(b.user_name));
      const sub = persons.reduce((a, u) => ({ in: a.in + u.inSum, out: a.out + u.outSum, count: a.count + u.txns.length }), { in: 0, out: 0, count: 0 });
      return { project, persons, sub };
    }).filter((g) => g.persons.length > 0).sort((a, b) => a.project.localeCompare(b.project));
    const grand = groups.reduce((a, g) => ({ in: a.in + g.sub.in, out: a.out + g.sub.out, count: a.count + g.sub.count }), { in: 0, out: 0, count: 0 });
    return { groups, grand };
  }, [rows, reportDateFrom, reportDateTo, reportSearch]);

  // 2026-09-29 (user request): filter tabel Report per PROYEK dan/atau per
  // STAFF ("" = semua; bisa dipakai sendiri-sendiri atau digabung). Total &
  // Export Excel mengikuti filter yang aktif.
  const [reportProjectFilter, setReportProjectFilter] = useState("");
  const [reportStaffFilter, setReportStaffFilter] = useState("");
  const reportProjectOptions = useMemo(
    () => [...new Set(reportLedger.groups.map((g) => g.project))],
    [reportLedger],
  );
  const reportStaffOptions = useMemo(
    () => [...new Set(reportLedger.groups.flatMap((g) => g.persons.map((u) => u.user_name)))].sort(),
    [reportLedger],
  );
  const reportView = useMemo(() => {
    let groups = reportLedger.groups;
    if (reportProjectFilter) groups = groups.filter((g) => g.project === reportProjectFilter);
    if (reportStaffFilter) {
      groups = groups
        .map((g) => ({ ...g, persons: g.persons.filter((u) => u.user_name === reportStaffFilter) }))
        .filter((g) => g.persons.length > 0);
    }
    const grand = groups.reduce(
      (a, g) => ({ in: a.in + g.sub.in, out: a.out + g.sub.out, count: a.count + g.sub.count }),
      { in: 0, out: 0, count: 0 },
    );
    return { groups, grand };
  }, [reportLedger, reportProjectFilter, reportStaffFilter]);

  const pcCellVal = (t, key) => {
    switch (key) {
      case "date": return <span className="tabular-nums whitespace-nowrap">{fmtDate(t.date) || "—"}</span>;
      case "description": return <span className="text-slate-800">{t.description}</span>;
      case "credit": return t.credit > 0 ? <span className="tabular-nums font-semibold text-emerald-700">{formatIDR(t.credit)}</span> : "";
      case "debit": return t.debit > 0 ? <span className="tabular-nums font-semibold text-rose-700">{formatIDR(t.debit)}</span> : "";
      case "balance": return <span className="tabular-nums font-semibold text-[#0B1D3A]">{formatIDR(t.saldo)}</span>;
      case "doc_no": return t.doc_no || "—";
      case "pic_name": return t.pic || "—";
      case "keterangan_pembelian": return t.ket || "—";
      case "project_name": return t.project;
      case "invoice_no": return t.voucher_date ? <span className="tabular-nums whitespace-nowrap">{fmtDate(t.voucher_date)}</span> : "—";
      case "coa_code": return <CoaCellEditor txn={t} coaOptions={coaOptions} canEdit={isFinanceLead} onSaved={load} />;
      case "coa_name": return t.coa_name || "—";
      default: return "—";
    }
  };

  const [exportingReportPdf, setExportingReportPdf] = useState(false);
  const handleReportExportPdf = async () => {
    try {
      setExportingReportPdf(true);
      await exportPettyCashReportPdf(reportView.groups, reportView.grand);
      toast.success("Export PDF diunduh.");
    } catch {
      toast.error("Gagal export PDF.");
    } finally {
      setExportingReportPdf(false);
    }
  };

  const handleReportExport = async () => {
    try {
      setExportingReport(true);
      await exportPettyCashReport(reportView.groups, reportView.grand);
      toast.success("Export Excel diunduh.");
    } catch (err) {
      let msg = "Gagal export Excel.";
      try {
        const t = await err?.response?.data?.text?.();
        if (t) msg = JSON.parse(t).detail || msg;
      } catch { /* abaikan */ }
      toast.error(msg);
    } finally { setExportingReport(false); }
  };

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-4"}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* 2026-06-20: mode embedded (di dalam Kas & Bank) menyembunyikan judul halaman
            untuk menghindari double-header; tombol aksi tetap tampil. */}
        {!embedded && (
        <div>
          <p className="overline text-slate-500">Requests</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A] flex items-center gap-2">
            <Wallet size={24} className="text-[#FF5A1F]" /> Weekly Petty Cash
          </h1>
        </div>
        )}
        <div className="flex items-center gap-2">
          <SearchableMultiSelect
            testid="btn-pc-project-filter"
            values={pfIds}
            onChange={setPfIds}
            options={projects.map((p) => ({ value: p.id, label: projectLabel(p) }))}
            allLabel="Semua Proyek"
          />
          <SearchableMultiSelect
            testid="btn-pc-user-filter"
            values={ufIds}
            onChange={setUfIds}
            options={submitters.map((s) => ({ value: s.user_id, label: s.user_name }))}
            allLabel="Semua Staff"
          />

          <Button
            data-testid="btn-open-new-pc"
            onClick={() => setOpenNew(true)}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white uppercase text-xs tracking-wider font-semibold h-9"
          >
            <Plus size={14} className="mr-1" /> New Petty Cash
          </Button>
          <Button
            data-testid="btn-open-return-pc"
            onClick={() => setReturnOpen(true)}
            disabled={!myBalance || (myBalance?.balance || 0) <= 0}
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white uppercase text-xs tracking-wider font-semibold h-9 disabled:opacity-50 disabled:cursor-not-allowed"
            title={(!myBalance || (myBalance?.balance || 0) <= 0) ? "Saldo Anda 0 — tidak ada kas untuk dikembalikan" : "Kembalikan sisa kas ke perusahaan"}
          >
            <ArrowLeftCircle size={14} className="mr-1" /> Kembalikan Kas
          </Button>
          <Button
            data-testid="btn-toggle-returns-history"
            onClick={() => setShowReturnsHistory((s) => !s)}
            variant="outline"
            className="rounded-none h-9 border border-emerald-700 text-emerald-700 hover:bg-emerald-50 text-[10px] uppercase tracking-wider font-semibold"
            title="Lihat riwayat pengembalian kas"
          >
            <RotateCcw size={12} className="mr-1" /> {showReturnsHistory ? "Sembunyikan" : "Riwayat"} Return
          </Button>
          {pcPerms.view && (
            <Button
              data-testid="btn-open-audit-trail"
              onClick={() => { setAuditTrailPcId(null); setAuditTrailOpen(true); }}
              variant="outline"
              className="rounded-none h-9 border border-violet-700 text-violet-700 hover:bg-violet-50 text-[10px] uppercase tracking-wider font-semibold"
              title="Forensik lengkap setiap tindakan Petty Cash (create/approve/reject/reopen/assign/verify/transfer/delete/restore)"
            >
              <ShieldCheck size={12} className="mr-1" /> Audit Trail
            </Button>
          )}
          {pcPerms.export && (
            <Button
              data-testid="btn-open-export-dialog"
              onClick={() => setExportOpen(true)}
              className="rounded-none h-9 border border-[#0B1D3A] bg-white text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white text-[10px] uppercase tracking-wider font-semibold"
              title="Export petty cash sesuai periode minggu"
            >
              <FileDown size={12} className="mr-1" /> Export
            </Button>
          )}
          {/* 2026-10-14 (user request): tombol antrean Verifikasi 2 (Penetapan COA)
              — sebelumnya halaman /petty-cash/penetapan-coa tidak punya pintu masuk
              dari Kas & Bank, sehingga pemegang akses verify_coa tidak menemukannya. */}
          {pcPerms.verify_coa && (
            <Button
              data-testid="btn-open-penetapan-coa"
              onClick={() => navigate("/petty-cash/penetapan-coa")}
              variant="outline"
              className="rounded-none h-9 border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white text-[10px] uppercase tracking-wider font-semibold"
              title="Antrean Verifikasi 2 — penetapan akun COA untuk item yang sudah disahkan (Verifikasi 1)"
            >
              <CheckSquare size={12} className="mr-1" /> Verifikasi 2 (COA)
            </Button>
          )}
          {(pcPerms.approve || pcPerms.reject) && selectedIds.size > 0 && (
            <Button
              data-testid="btn-bulk-approve"
              onClick={bulkApproveSelected}
              disabled={bulkBusy}
              className="rounded-none h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-wider font-semibold inline-flex items-center gap-1"
            >
              <CheckSquare size={13} /> Approve {selectedIds.size} Selected
            </Button>
          )}
          {isBOD && statusFilter === "deleted" && counts.deleted > 0 && (
            <Button
              data-testid="btn-purge-old-trash"
              onClick={purgeOldTrash}
              className="rounded-none h-9 border border-rose-500 bg-white text-rose-700 hover:bg-rose-600 hover:text-white text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"
              title="Hard-delete pengajuan yang sudah lama di trash"
            >
              <Trash2 size={12} /> Purge Old
            </Button>
          )}
        </div>
      </div>

      {/* 2026-06-20 (user request): sub-tab Request / Report di dalam Weekly Petty Cash. */}
      <div className="flex items-center gap-1" data-testid="pc-view-tabs">
        {[{ k: "request", l: "Request" }, { k: "report", l: "Report" }].map((t) => (
          <button key={t.k} type="button" data-testid={`pc-view-${t.k}`} onClick={() => setView(t.k)}
            className={`px-3 py-1.5 border text-[10px] uppercase tracking-wider font-semibold transition-colors ${view === t.k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A] hover:text-[#0B1D3A]"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Report: ledger transaksi per proyek → per staff, kolom mengikuti template Breakdown. */}
      {view === "report" && (
        <div className="bg-white border border-slate-200 overflow-hidden" data-testid="pc-report">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-700 font-bold">Report Weekly Petty Cash — Transaksi per Proyek per Staff</div>
              <div className="text-[10px] text-slate-500 italic">Kolom mengikuti template Breakdown/Buku Bank · Hijau = pengisian kas (CREDIT) · Ungu = realisasi pengeluaran (DEBIT) · SALDO = saldo berjalan per staff</div>
            </div>
            <div className="flex items-center gap-2">
              <input type="date" value={reportDateFrom} onChange={(e) => setReportDateFrom(e.target.value)} data-testid="pc-report-date-from"
                title="Dari tanggal transaksi" className="border border-slate-300 px-2 py-1 text-[10px] bg-white" />
              <span className="text-[10px] text-slate-400">s/d</span>
              <input type="date" value={reportDateTo} onChange={(e) => setReportDateTo(e.target.value)} data-testid="pc-report-date-to"
                title="Sampai tanggal transaksi" className="border border-slate-300 px-2 py-1 text-[10px] bg-white" />
              <input type="text" value={reportSearch} onChange={(e) => setReportSearch(e.target.value)} placeholder="Cari transaksi…" data-testid="pc-report-search"
                title="Cari di deskripsi, voucher, COA, PIC, keterangan, staff, proyek"
                className="border border-slate-300 px-2 py-1 text-[10px] bg-white w-40" />
              <select value={reportProjectFilter} onChange={(e) => setReportProjectFilter(e.target.value)} data-testid="pc-report-filter-project"
                title="Filter per proyek"
                className="border border-slate-300 px-2 py-1 text-[10px] bg-white max-w-[200px]">
                <option value="">Semua Proyek</option>
                {reportProjectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={reportStaffFilter} onChange={(e) => setReportStaffFilter(e.target.value)} data-testid="pc-report-filter-staff"
                title="Filter per staff"
                className="border border-slate-300 px-2 py-1 text-[10px] bg-white max-w-[200px]">
                <option value="">Semua Staff</option>
                {reportStaffOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {(reportProjectFilter || reportStaffFilter || reportDateFrom || reportDateTo || reportSearch) && (
                <button type="button" onClick={() => { setReportProjectFilter(""); setReportStaffFilter(""); setReportDateFrom(""); setReportDateTo(""); setReportSearch(""); }} data-testid="pc-report-filter-reset"
                  className="border border-slate-300 px-2 py-1 text-[9px] uppercase tracking-wider text-slate-500 hover:text-rose-600 hover:border-rose-300">
                  Reset
                </button>
              )}
              <button type="button" onClick={handleReportExport} disabled={exportingReport || reportView.groups.length === 0}
                data-testid="pc-report-export"
                title="Export Excel format template Buku Bank (saldo awal kuning, hijau/ungu) — data persis seperti tampilan (mengikuti filter aktif)"
                className="inline-flex items-center gap-1 px-2.5 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 text-[10px] uppercase tracking-wider font-semibold transition-colors">
                <FileDown size={11} /> {exportingReport ? "Menyiapkan…" : "Export Excel"}
              </button>
              <button type="button" onClick={handleReportExportPdf} disabled={exportingReportPdf || reportView.groups.length === 0}
                data-testid="pc-report-export-pdf"
                title="Export PDF — data persis seperti tampilan (mengikuti filter aktif)"
                className="inline-flex items-center gap-1 px-2.5 py-1 border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-40 text-[10px] uppercase tracking-wider font-semibold transition-colors">
                <FileDown size={11} /> {exportingReportPdf ? "Menyiapkan…" : "Export PDF"}
              </button>
              <div className="text-right text-[10px] text-slate-500">{reportView.grand.count} transaksi · {reportView.groups.length} proyek</div>
            </div>
          </div>
          {reportView.groups.length === 0 ? (
            <div className="p-6 text-center text-[11px] text-slate-400" data-testid="pc-report-empty">
              {(reportProjectFilter || reportStaffFilter || reportDateFrom || reportDateTo || reportSearch)
                ? "Tidak ada transaksi yang cocok dengan filter proyek/staff/tanggal/pencarian terpilih."
                : "Tidak ada data petty cash pada filter saat ini."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]" data-testid="pc-report-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[9px] uppercase tracking-wider text-slate-600">
                  <tr>
                    {/* 2026-09-30 (user request): header sticky — judul kolom
                        tetap terlihat saat tabel panjang di-scroll ke bawah. */}
                    {pcReportCols.map((c) => (
                      <th key={c.key} className={`sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] px-3 py-2 ${PC_NUM_COLS.has(c.key) ? "text-right" : "text-left"}`} data-testid={`pc-report-col-${c.key}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportView.groups.map((g, gi) => [
                    <tr key={`pg-${gi}`} className="bg-slate-100/80 border-b border-slate-200" data-testid={`pc-report-group-${gi}`}>
                      {pcReportCols.map((c) => (
                        <td key={c.key} className={`px-3 py-1.5 ${PC_NUM_COLS.has(c.key) ? "text-right tabular-nums font-semibold text-slate-600" : ""} ${c.key === "description" ? "font-bold text-[#0B1D3A]" : ""}`}>
                          {c.key === "description" ? g.project
                            : c.key === "credit" ? formatIDR(g.sub.in)
                            : c.key === "debit" ? formatIDR(g.sub.out)
                            : c.key === "balance" ? formatIDR(g.sub.in - g.sub.out)
                            : ""}
                        </td>
                      ))}
                    </tr>,
                    ...g.persons.map((u, ui) => [
                      <tr key={`ps-${gi}-${ui}`} className="bg-slate-50 border-b border-slate-200" data-testid={`pc-report-staff-${gi}-${ui}`}>
                        {pcReportCols.map((c) => (
                          <td key={c.key} className={`px-3 py-1 ${PC_NUM_COLS.has(c.key) ? "text-right tabular-nums text-slate-500" : ""}`}>
                            {c.key === "description" ? <span className="pl-3 font-semibold text-slate-700">{u.user_name} <span className="font-normal text-slate-400">· {u.division}</span></span>
                              : c.key === "credit" ? formatIDR(u.inSum)
                              : c.key === "debit" ? formatIDR(u.outSum)
                              : c.key === "balance" ? <span className={`font-semibold ${u.saldo > 0 ? "text-amber-700" : "text-emerald-700"}`}>{formatIDR(u.saldo)}</span>
                              : ""}
                          </td>
                        ))}
                      </tr>,
                      ...u.txns.map((t, ti) => (
                        <tr key={`pt-${gi}-${ui}-${ti}`} className="border-b border-slate-100 hover:brightness-95"
                          style={{ backgroundColor: t.kind === "in" ? pcHexA(pcColors.green, 0.15) : pcHexA(pcColors.purple, 0.12) }}
                          data-testid={`pc-report-txn-${gi}-${ui}-${ti}`}>
                          {pcReportCols.map((c) => (
                            <td key={c.key} className={`px-3 py-1.5 ${PC_NUM_COLS.has(c.key) ? "text-right" : ""} ${c.key === "description" ? "pl-6" : ""}`} data-testid={`pc-report-cell-${c.key}-${gi}-${ui}-${ti}`}>{pcCellVal(t, c.key)}</td>
                          ))}
                        </tr>
                      )),
                    ]),
                  ])}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0B1D3A] text-white text-[11px]" data-testid="pc-report-grand-total">
                    {pcReportCols.map((c) => (
                      <td key={c.key} className={`px-3 py-2 font-bold ${PC_NUM_COLS.has(c.key) ? "text-right tabular-nums" : ""}`}>
                        {c.key === "description" ? <span className="uppercase tracking-wider">Grand Total</span>
                          : c.key === "credit" ? formatIDR(reportView.grand.in)
                          : c.key === "debit" ? formatIDR(reportView.grand.out)
                          : c.key === "balance" ? formatIDR(reportView.grand.in - reportView.grand.out)
                          : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Saldo banner: Total Diterima − Pengeluaran Realisasi = Saldo Tersedia */}
      {view === "request" && myBalance && (
        <div className="border border-slate-300 bg-gradient-to-r from-emerald-50 via-white to-slate-50 p-4 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="pc-page-balance-banner">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Diterima</div>
            <div className="tabular-nums text-lg font-bold text-slate-800" data-testid="pc-page-balance-transferred">{formatIDR(myBalance.total_transferred)}</div>
            <div className="text-[9px] text-slate-400">uang sudah masuk ke rekening Anda</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Pengeluaran Realisasi</div>
            <div className="tabular-nums text-lg font-bold text-rose-700" data-testid="pc-page-balance-spent">− {formatIDR(myBalance.total_actual_verified)}</div>
            <div className="text-[9px] text-slate-400">sudah lolos Verifikasi 1 (FAT)</div>
          </div>
          <div className="md:border-l md:border-slate-300 md:pl-4">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Saldo Tersedia</div>
            <div className={`tabular-nums text-2xl font-bold ${myBalance.balance >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="pc-page-balance-available">
              {formatIDR(myBalance.balance)}
            </div>
            <div className="text-[9px] text-slate-400">total semua project</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Menunggu Verifikasi 1</div>
            <div className="tabular-nums text-lg font-bold text-amber-700" data-testid="pc-page-balance-unverified">{formatIDR(myBalance.unverified_actual)}</div>
            <div className="text-[9px] text-slate-400">aktual sudah diisi, belum lolos Verifikasi 1</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Belum Isi Aktual</div>
            <div className="tabular-nums text-lg font-bold text-slate-700" data-testid="pc-page-balance-pending-count">{myBalance.pending_actual_fill_count}<span className="text-[10px] font-normal text-slate-500 ml-1">pengajuan</span></div>
            <div className="text-[9px] text-slate-400">sudah ditransfer, tunggu Anda</div>
          </div>
        </div>
      )}

      {/* 149-cw-r192 (user request): kotak "Pengeluaran Kantor (Overhead / Operating
          Expense)" DIHAPUS dari halaman ini — data overhead tetap bisa difilter manual
          lewat filter project + halaman Laporan Overhead. Endpoint
          GET /petty-cash/office-summary tetap ada di backend. */}

      {/* 149-cw-r189: Rekap saldo semua staff — hanya user dgn perm approve.
          Menampilkan sisa saldo per pengaju petty cash (transfer − terverifikasi − return). */}
      {view === "request" && pcPerms.approve && staffSummary && staffSummary.length > 0 && (
        <div className="border border-slate-300 bg-white p-4" data-testid="pc-staff-summary">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-700 font-bold">Rekap Saldo Staf</div>
              <div className="text-[10px] text-slate-500 italic">Ringkasan saldo pertanggungjawaban petty cash staf. Klik nama staf untuk melihat rincian per proyek dan transaksi pembentuknya. Label "Belum dapat dipastikan" menunjukkan adanya data atau status yang memerlukan pemeriksaan.</div>
              <a href="/finance-report/rekonsiliasi-kas-staf" data-testid="pc-staff-open-pc-recon" className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline">Rekonsiliasi Kas Staf →</a>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">Total Saldo Pertanggungjawaban Semua Staf</div>
              <div className="tabular-nums text-xl font-bold text-[#0B1D3A]" data-testid="pc-staff-summary-total">
                {formatIDR(staffSummary.reduce((s, r) => s + (r.balance || 0), 0))}
              </div>
              <div className="text-[9px] text-slate-500">{staffSummary.length} staf</div>
            </div>
          </div>
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full text-xs" data-testid="pc-staff-summary-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Staf</th>
                  <th className="text-left px-3 py-2">Divisi</th>
                  <th className="text-right px-3 py-2">Dana Diterima</th>
                  <th className="text-right px-3 py-2">Realisasi Terverifikasi</th>
                  <th className="text-right px-3 py-2">Pengembalian Kas Terverifikasi</th>
                  <th className="text-right px-3 py-2">Saldo Pertanggungjawaban</th>
                  <th className="text-left px-3 py-2">Aktivitas Terakhir</th>
                </tr>
              </thead>
              <tbody>
                {staffSummary.map((s) => {
                  const expanded = !!expandedStaff[s.user_id];
                  const byProj = Array.isArray(s.by_project) ? s.by_project : [];
                  return [
                    // 2026-09-23 (user request): klik baris = toggle dropdown saja.
                    // Detail ledger dibuka dari klik NAMA PROJECT di dropdown.
                    <tr key={s.user_id} className="border-b border-slate-100 hover:bg-amber-50/60 cursor-pointer transition-colors" data-testid={`pc-staff-summary-row-${s.user_id}`}
                        onClick={() => toggleStaffExpand(s.user_id)} title="Klik untuk buka/tutup rincian per proyek">
                      <td className="px-3 py-2 font-semibold text-[#0B1D3A]">
                        <button type="button"
                          data-testid={`pc-staff-expand-${s.user_id}`}
                          onClick={(e) => { e.stopPropagation(); toggleStaffExpand(s.user_id); }}
                          title={expanded ? "Sembunyikan rincian per proyek" : "Lihat rincian per proyek"}
                          className="inline-flex items-center gap-1 underline decoration-dotted decoration-slate-300 underline-offset-4 hover:text-[#FF5A1F] transition-colors">
                          {expanded ? <ChevronDown size={12} className="text-[#FF5A1F]" /> : <ChevronRight size={12} className="text-slate-400" />}
                          {s.user_name}
                        </button>
                        {s.engine?.has_legacy && (
                          <span className="ml-1.5 inline-flex px-1 py-0 text-[8px] uppercase tracking-wider border border-amber-300 bg-amber-50 text-amber-800 align-middle"
                            data-testid={`pc-staff-legacy-badge-${s.user_id}`}
                            title={`Estimasi dana historis ${formatIDR(s.engine?.dana_diterima_estimasi_legacy ?? 0)} — nominal transfer historis belum diketahui.`}>historis</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{s.division}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatIDR(s.total_transferred || 0)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatIDR(s.total_actual_verified || 0)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{formatIDR(s.total_returned || 0)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-bold ${(s.balance || 0) > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid={`pc-staff-balance-${s.user_id}`}>
                        {formatIDR(s.balance || 0)}
                        {s.engine?.saldo_pj_completeness === "belum_dapat_dipastikan" && (
                          <div className="text-[9px] font-normal text-rose-700" data-testid={`pc-staff-balance-uncertain-${s.user_id}`}
                            title={`${buildKetidakpastianTooltip(s.engine)}${s.engine?.saldo_pj_strict != null ? ` Pembanding — tanpa estimasi historis: ${formatIDR(s.engine.saldo_pj_strict)} · dengan estimasi historis: ${formatIDR(s.engine?.saldo_pj_termasuk_estimasi ?? 0)}.` : ""}`}>Belum dapat dipastikan</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-400 text-[10px]">{s.last_activity_at ? new Date(s.last_activity_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                    </tr>,
                    ...(expanded ? [
                      ...(byProj.length > 0
                        ? byProj.map((pj, pi) => (
                            <tr key={`${s.user_id}-proj-${pi}`} className="border-b border-slate-100 bg-slate-50/70" data-testid={`pc-staff-projrow-${s.user_id}-${pi}`}>
                              <td className="px-3 py-1.5 pl-8">
                                <button type="button"
                                  data-testid={`pc-staff-projdetail-${s.user_id}-${pi}`}
                                  onClick={(e) => { e.stopPropagation(); setLedgerUserId(s.user_id); setLedgerProject({ id: pj.project_id || null, name: pj.project_name }); }}
                                  title={`Buka detail ledger ${s.user_name} untuk ${pj.project_name}`}
                                  className="text-slate-700 font-medium underline decoration-dotted decoration-slate-300 underline-offset-4 hover:text-[#FF5A1F] transition-colors text-left">
                                  {pj.project_name}
                                </button>
                              </td>
                              <td className="px-3 py-1.5 text-slate-400 text-[10px] italic">proyek</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{formatIDR(pj.transferred || 0)}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{formatIDR(pj.verified || 0)}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">—</td>
                              <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${(pj.sisa || 0) > 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid={`pc-staff-projsisa-${s.user_id}-${pi}`}>{formatIDR(pj.sisa || 0)}</td>
                              <td className="px-3 py-1.5"></td>
                            </tr>
                          ))
                        : [
                            <tr key={`${s.user_id}-proj-empty`} className="border-b border-slate-100 bg-slate-50/70" data-testid={`pc-staff-projrow-${s.user_id}-empty`}>
                              <td colSpan={7} className="px-3 py-2 pl-8 text-slate-400 italic text-[11px]">Belum ada aktivitas kas per proyek untuk staf ini.</td>
                            </tr>,
                          ]),
                      <tr key={`${s.user_id}-proj-note`} className="border-b border-slate-200 bg-slate-50/70" data-testid={`pc-staff-projnote-${s.user_id}`}>
                        <td colSpan={7} className="px-3 py-1.5 pl-8 text-[9px] text-slate-400 italic">
                          Saldo per proyek = Dana Diterima − Realisasi Terverifikasi. Pengembalian kas tidak terikat proyek — hanya memotong saldo di baris total staf.
                        </td>
                      </tr>,
                    ] : []),
                  ];
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "request" && (<>
      <div className={`grid grid-cols-2 gap-2 ${isBOD ? "md:grid-cols-6" : "md:grid-cols-5"}`} data-testid="pc-stats">
        {[
          { k: "all", l: "Semua", c: counts.all },
          { k: "actual_incomplete", l: "Aktual Belum Lengkap", c: counts.actual_incomplete },
          { k: "pending", l: "Menunggu", c: counts.pending },
          { k: "approved", l: "Disetujui", c: counts.approved },
          { k: "awaiting_transfer", l: "Menunggu Transfer", c: counts.approved_not_transferred },
          { k: "rejected", l: "Ditolak", c: counts.rejected },
          ...(isBOD ? [{ k: "deleted", l: "Terhapus", c: counts.deleted }] : []),
        ].map((s) => (
          <button
            key={s.k}
            data-testid={`pc-tab-${s.k}`}
            onClick={() => setStatusFilter(s.k)}
            className={`text-left px-3 py-2 border transition-colors ${statusFilter === s.k ? (s.k === "deleted" ? "bg-rose-700 text-white border-rose-700" : "bg-[#0B1D3A] text-white border-[#0B1D3A]") : (s.k === "deleted" ? "bg-white text-rose-700 border-rose-300 hover:border-rose-700" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]")}`}
          >
            <div className="text-[10px] uppercase tracking-wider opacity-70">{s.l}</div>
            <div className="font-display text-2xl tabular-nums">{s.c}</div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-xs" data-testid="pc-table">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-2 py-2 w-8">
                {isFinanceLead ? (
                  <button
                    data-testid="btn-select-all"
                    onClick={() => {
                      const pendings = rows.filter((r) => r.status === "pending" && r.user_id !== user?.id).map((r) => r.id);
                      if (pendings.every((id) => selectedIds.has(id))) setSelectedIds(new Set());
                      else setSelectedIds(new Set(pendings));
                    }}
                    className="text-slate-500 hover:text-[#FF5A1F]"
                    title="Pilih semua pending"
                  >{selectedIds.size > 0 ? <CheckSquare size={14} /> : <Square size={14} />}</button>
                ) : null}
              </th>
              <SortTh label="Week / Period" k="week" />
              <SortTh label="Project" k="project" />
              <SortTh label="Submitter" k="submitter" />
              <SortTh label="Total" k="total" className="text-right" />
              <SortTh label="Status" k="status" />
              <SortTh label="Transfer" k="transfer" />
              {/* 149-cw-r177: kolom Check — verifikasi cepat realisasi oleh user
                  ber-perm verify_actual, langsung dari tabel tanpa buka dialog. */}
              <th className="text-center px-2 py-2">Check</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedVisible.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-400 py-8">Tidak ada pengajuan.</td></tr>
            )}
            {sortedVisible.map((r) => (
              <PettyCashRow
                key={r.id}
                r={r}
                user={user}
                isBOD={isBOD}
                isFinanceLead={isFinanceLead}
                pcPerms={pcPerms}
                reviewerOptions={reviewerOptions}
                selected={selectedIds.has(r.id)}
                onToggleSelected={toggleSelected}
                onOpenDetail={openDetailDialog}
                onOpenReview={(row, action) => { setReviewing(row); setReviewAction(action); setReviewNote(""); }}
                onOpenEdit={(row) => {
                  setEditingPc(row);
                  setEditItems((row.items || []).map((it) => ({
                    name: it.name || "",
                    qty: it.qty ?? 1,
                    unit_price: it.unit_price ?? 0,
                    note: it.note || "",
                  })));
                  setEditNote("");
                }}
                onOpenTransfer={(row) => { setTransferring(row); setTransferDate(""); setTransferNote(""); setTransferSourceKey(""); setTransferAmount(null); }}
                onOpenDelete={setConfirmDel}
                onAssignReviewer={assignReviewer}
                onRestore={async (row) => {
                  try {
                    await api.post(`/petty-cash/${row.id}/restore`);
                    toast.success("Petty cash dipulihkan");
                    load();
                  } catch (e) { toast.error(formatDetail(e) || "Restore gagal"); }
                }}
                onReopen={async (row) => {
                  const reason = window.prompt("Alasan Reopen pengajuan ini? (wajib, min 3 karakter)");
                  if (!reason || reason.trim().length < 3) return;
                  try {
                    await api.post(`/petty-cash/${row.id}/reopen`, { reason: reason.trim() });
                    toast.success("Pengajuan di-reopen ke Pending. Approval ulang diperlukan.");
                    load();
                  } catch (e) { toast.error(formatDetail(e) || "Reopen gagal"); }
                }}
                onOpenActual={openActualDialog}
                onOpenRealloc={openReallocDialog}
                onVerify={verifyActual}
                onQuickVerify={async (row) => {
                  // 149-cw-r180: kolom Check = tahapan TERPISAH pasca-verifikasi.
                  // 1-klik check via POST /petty-cash/{id}/check — backend validate
                  // perm `check`, harus sudah terverifikasi, dan bukan pengaju sendiri.
                  try {
                    await api.post(`/petty-cash/${row.id}/check`);
                    toast.success(`${row.week_label} · ${row.user_name} sudah di-check`);
                    load();
                  } catch (e) { toast.error(formatDetail(e.response?.data?.detail || e) || "Check gagal"); }
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
      </>)}

      <NewPettyCashDialog
        open={openNew}
        onOpenChange={setOpenNew}
        defaultProjectId={pfIds.length === 1 ? pfIds[0] : ""}
        onCreated={load}
        existingRows={rows}
        currentUserId={user?.id}
      />

      {/* Actual Expense Dialog — pengaju isi realisasi per item setelah transfer diterima */}
      <PettyCashReallocateDialog
        open={!!reallocPc}
        pc={reallocPc}
        onCancel={() => setReallocPc(null)}
        onSubmit={submitRealloc}
      />

      <PettyCashActualDialog
        open={!!actualPc}
        pc={actualPc}
        items={actualItems}
        docNote={actualDocNote}
        onItemFieldChange={setActualItemField}
        onDocNoteChange={setActualDocNote}
        onCancel={() => { setActualPc(null); setActualItems({}); setActualDocNote(""); }}
        onSubmit={submitActual}
        onProofChanged={load}
      />

      <PettyCashDetailDialog
        open={!!detail}
        pc={detail}
        user={user}
        isApprover={isFinanceLead}
        onClose={() => { setDetail(null); setHighlightItemId(null); }}
        onChanged={(updated) => { if (updated) setDetail(updated); load(); }}
        highlightItemId={highlightItemId}
      />


      <PettyCashReviewDialog
        open={!!reviewing}
        pc={reviewing}
        action={reviewAction}
        note={reviewNote}
        onNoteChange={setReviewNote}
        onCancel={() => setReviewing(null)}
        onSubmit={submitReview}
      />

      <PettyCashTransferDialog
        open={!!transferring}
        pc={transferring}
        transferDate={transferDate}
        transferNote={transferNote}
        transferBankId={transferBankId}
        transferBankOptions={transferBankOptions}
        transferSourceKey={transferSourceKey}
        transferAmount={transferAmount}
        onDateChange={setTransferDate}
        onNoteChange={setTransferNote}
        onBankChange={setTransferBankId}
        onSourceChange={setTransferSourceKey}
        onTransferAmountChange={setTransferAmount}
        onCancel={() => { setTransferring(null); setTransferSourceKey(""); setTransferAmount(null); }}
        onSubmit={submitTransfer}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => { if (!o) setConfirmDel(null); }}
        title="Hapus petty cash?"
        description="Pengajuan akan dihapus permanen."
        confirmLabel="Hapus"
        onConfirm={doDelete}
      />

      {/* Edit Items Dialog (BOD / FAT Lv1-2) */}
      <PettyCashEditDialog
        open={!!editingPc}
        pc={editingPc}
        items={editItems}
        note={editNote}
        onItemsChange={setEditItems}
        onNoteChange={setEditNote}
        onCancel={() => { setEditingPc(null); setEditItems([]); setEditNote(""); }}
        onSubmit={submitEditPc}
      />

      {/* Bulk Export dialog — pick week range + format */}
      {showReturnsHistory && (
        <PettyCashReturnsHistory
          isReviewer={isFinanceLead}
          currentUserId={user?.id}
          onChanged={() => { loadBalance(); load(); }}
        />
      )}

      <PettyCashReturnDialog
        open={returnOpen}
        currentBalance={myBalance?.balance || 0}
        effectiveCap={myBalance?.effective_return_cap ?? myBalance?.balance ?? 0}
        pendingReturns={myBalance?.pending_returns || 0}
        onCancel={() => setReturnOpen(false)}
        onSuccess={() => { setReturnOpen(false); loadBalance(); load(); }}
      />

      {/* 149-cw-r188: export mengikuti filter project halaman — tidak ada picker ulang. */}
      <PettyCashExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        currentProjectId={pfIds.join(",")}
        currentProjectNames={pfNames}
        currentUserIds={ufIds.join(",")}
        currentUserNames={ufNames}
      />
      <PettyCashAuditTrailViewer
        open={auditTrailOpen}
        onClose={() => { setAuditTrailOpen(false); setAuditTrailPcId(null); }}
        initialPettyCashId={auditTrailPcId}
      />

      {/* Ledger drill-down dari row Rekap Saldo Staff (user request 2026-06-10). */}
      <StaffCashLedgerDialog userId={ledgerUserId} projectId={ledgerProject?.id || null} projectName={ledgerProject?.name || null} onClose={() => { setLedgerUserId(null); setLedgerProject(null); }} />
    </div>
  );
}
